/**
 * Google Analytics 4 for My Booking.
 *
 * The tag is bootstrapped in _Layout.cshtml from Olympic:Analytics:GoogleMeasurementId (so Test
 * and Live can point at different properties; unset = no tag) with send_page_view off — this
 * SPA reports its own page views on route change. Off production the tag runs in debug_mode so
 * events show up live in GA4 → Admin → DebugView.
 *
 * Funnel design — GA4's recommended ecommerce events, so the standard Monetisation reports
 * and Funnel Explorations work without custom setup:
 *
 *   view_item_list   excursion shop opened / car results shown
 *   select_item      "Choose a day" (excursion) / car card opened
 *   view_item        "More details" modal / car detail modal
 *   add_to_cart      excursion or car added to the basket
 *   remove_from_cart line removed at checkout
 *   view_cart        checkout page opened with a basket
 *   begin_checkout   checkout page opened (same moment — the basket IS the checkout)
 *   add_payment_info card step opened
 *   purchase         extras confirmed (transaction_id = the (ancillary) booking reference; random without consent)
 *
 * plus custom events for the steps between (login, driver/age prompts, day + pick-up choices,
 * balance payments, document downloads). With analytics consent every event carries booking_reference so funnels can be
 * segmented per booking without PII (no names, emails or DOBs are ever sent).
 */

const cfg = () => (typeof window !== "undefined" ? window.olympicAnalytics : null) || {};
const hasGtag = () => typeof window !== "undefined" && typeof window.gtag === "function";

let bookingReference = null;
let context = {};

// Analytics consent ("Performance and Analytics" in the cookie banner). _Layout.cshtml reads a stored choice into
// window.olyConsent before any tag runs; the banner (oly-cookies.js, kept identical to booking./www.) fires the
// cookieconsent "cc:onConsent" / "cc:onChange" window events when the visitor chooses or changes their mind.
const analyticsConsent = () => {
    try {
        if (typeof window.CookieConsent?.acceptedCategory === "function" && window.CookieConsent.validConsent?.()) {
            return window.CookieConsent.acceptedCategory("analytics");
        }
    } catch { /* fall back to the page-load read */ }
    return !!(typeof window !== "undefined" && window.olyConsent?.analytics);
};

// The booking reference reaches GA4 only with analytics consent, however it is sent: user_id, the
// booking_reference user property, the booking_reference event parameter and the purchase transaction_id.
// Without consent GA4 runs cookieless (Consent Mode) and gets none of them.
const consentedReference = () => (bookingReference && analyticsConsent() ? bookingReference : null);

/** Raw event send. Silently a no-op when the tag isn't loaded (dev without an id, blockers). */
export const track = (eventName, params = {}) => {
    const payload = { ...context, ...params };
    const reference = consentedReference();
    if (reference) payload.booking_reference = reference;
    if (cfg().debug) {
        // eslint-disable-next-line no-console
        console.debug("[analytics]", eventName, payload);
    }
    if (!hasGtag()) return;
    try {
        window.gtag("event", eventName, payload);
    } catch { /* analytics must never break the site */ }
};

/** SPA page view — called on every route change. */
export const trackPageView = (path, title) => {
    if (!hasGtag()) return;
    const reference = consentedReference();
    try {
        window.gtag("event", "page_view", {
            page_path: path,
            page_location: window.location.href,
            page_title: title || document.title,
            ...(reference ? { booking_reference: reference } : {}),
        });
    } catch { /* ignore */ }
};

// user_id and the booking_reference user property: applied when a booking is set, re-applied when consent is
// granted mid-session and cleared when it is withdrawn. gtag('set'), not a second gtag('config'): re-configuring
// the tag after load never put uid on the wire.
const applyBookingIdentity = () => {
    if (!hasGtag()) return;
    const reference = consentedReference();
    try {
        window.gtag("set", { user_id: reference });
        window.gtag("set", "user_properties", { booking_reference: reference });
    } catch { /* ignore */ }
};

if (typeof window !== "undefined") {
    const onConsentChange = () => {
        if (window.olyConsent) window.olyConsent.analytics = analyticsConsent();
        applyBookingIdentity();
    };
    window.addEventListener("cc:onConsent", onConsentChange);
    window.addEventListener("cc:onChange", onConsentChange);
}

/**
 * Identify the session by booking. With analytics consent the reference is GA's user_id (one customer
 * journey across devices) and a user property for segmentation (see applyBookingIdentity). Pass null on logout.
 */
export const setBooking = (reference, props = {}) => {
    bookingReference = reference || null;
    context = reference ? { ...props } : {};
    if (!hasGtag()) return;
    applyBookingIdentity();
    try {
        window.gtag("set", "user_properties", {
            destination: props.destination || null,
            days_to_departure: props.daysToDeparture ?? null,
            booking_status: props.status || null,
        });
    } catch { /* ignore */ }
};

// Purchase transaction_id is a booking reference; without analytics consent GA4 gets a random id instead
// (still unique per purchase, so revenue is counted once, but not linked to the booking).
const purchaseTransactionId = (transactionId) => {
    if (analyticsConsent()) return transactionId;
    try {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    } catch { /* fall through */ }
    return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// ── ecommerce item builders ─────────────────────────────────────────────────────────────────
export const excursionItem = (excursion, extra = {}) => ({
    item_id: `EXC-${excursion.extraId}`,
    item_name: excursion.name,
    item_category: "Excursion",
    item_category2: excursion.baseDestination || undefined,
    price: Number(excursion.perPersonPrice || excursion.price || 0),
    quantity: 1,
    ...extra,
});

export const carItem = (car, extra = {}) => ({
    item_id: `CAR-${(car.vehicleDescription || "car").replace(/\s+/g, "-").slice(0, 60)}`,
    item_name: car.vehicleDescription,
    item_category: "Car hire",
    item_brand: car.supplier || undefined,
    price: Number(car.price || car.estimatedTotal || 0),
    quantity: 1,
    ...extra,
});

export const basketItems = (basket) => [
    ...(basket?.excursions || []).map((l) => ({
        item_id: `EXC-${l.extraId}`,
        item_name: l.name,
        item_category: "Excursion",
        item_category2: l.baseDestination || undefined,
        item_variant: l.useDate ? String(l.useDate).slice(0, 10) : undefined,
        price: Number(l.estimatedTotal || 0),
        quantity: 1,
    })),
    ...(basket?.car ? [{
        item_id: `CAR-${(basket.car.vehicleDescription || "car").replace(/\s+/g, "-").slice(0, 60)}`,
        item_name: basket.car.vehicleDescription,
        item_category: "Car hire",
        item_brand: basket.car.supplier || undefined,
        price: Number(basket.car.estimatedTotal || 0),
        quantity: 1,
    }] : []),
];

const money = (v) => Math.round(Number(v || 0) * 100) / 100;

// ── ecommerce funnel ────────────────────────────────────────────────────────────────────────
export const ecommerce = {
    viewItemList: (listId, listName, items) =>
        track("view_item_list", { item_list_id: listId, item_list_name: listName, items: items.slice(0, 50) }),
    selectItem: (listId, listName, item) =>
        track("select_item", { item_list_id: listId, item_list_name: listName, items: [item] }),
    viewItem: (item) => track("view_item", { currency: "GBP", value: money(item.price), items: [item] }),
    addToCart: (item, value) => track("add_to_cart", { currency: "GBP", value: money(value ?? item.price), items: [item] }),
    removeFromCart: (item, value) => track("remove_from_cart", { currency: "GBP", value: money(value ?? item.price), items: [item] }),
    viewCart: (basket) => track("view_cart", { currency: "GBP", value: money(basket?.estimatedTotal), items: basketItems(basket) }),
    beginCheckout: (basket) => track("begin_checkout", { currency: "GBP", value: money(basket?.estimatedTotal), items: basketItems(basket) }),
    addPaymentInfo: (basket, total) =>
        track("add_payment_info", { currency: "GBP", value: money(total ?? basket?.estimatedTotal), payment_type: "card", items: basketItems(basket) }),
    purchase: (transactionId, total, basket, extra = {}) =>
        track("purchase", { transaction_id: purchaseTransactionId(transactionId), currency: "GBP", value: money(total), items: basketItems(basket), ...extra }),
};
